import React, { useMemo, useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Grid, Breadcrumb } from 'antd';
import {
  TeamOutlined,
  DashboardOutlined,
  MailOutlined,
  ShopOutlined,
  DollarOutlined,
  FileTextOutlined,
  TagsOutlined,
  CustomerServiceOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loggedOut, selectAuth } from '../../store/authSlice';
import { ROLES, ROLE_NAME_LABELS } from '../../Constants/enums';
import velorMark from '../../assets/velor-mark.jpg';

const CRUMB_LABELS = {
  '/admin': 'Platform Administration',
  '/admin/users': 'Administration Users',
  '/cio': 'CIO Office',
  '/cio/invitations': 'Onboarding Invitations',
  '/cio/customers': 'Customers',
  '/cio/support': 'Tech Support & Issues',
  '/cfo': 'CFO Office',
  '/cfo/pricing-plans': 'Pricing Plans',
  '/cfo/invoices': 'Invoices',
};

const buildCrumbs = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  const items = [];
  let acc = '';
  parts.forEach((p) => {
    acc += `/${p}`;
    items.push({ path: acc, label: CRUMB_LABELS[acc] || (/^\d+$/.test(p) ? 'Detail' : p) });
  });
  return items;
};

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;

const buildMenu = (roles) => {
  const has = (r) => roles.includes(r);
  const isPlatform = has(ROLES.PLATFORM_ADMIN);
  const items = [];

  if (isPlatform) {
    items.push({
      key: 'grp-platform',
      label: 'Platform Administration',
      type: 'group',
      children: [
        { key: '/admin/users', icon: <TeamOutlined />, label: 'Administration Users' },
      ],
    });
  }

  if (isPlatform || has(ROLES.CIO)) {
    items.push({
      key: 'grp-cio',
      label: 'CIO Office',
      type: 'group',
      children: [
        { key: '/cio', icon: <DashboardOutlined />, label: 'Dashboard' },
        { key: '/cio/invitations', icon: <MailOutlined />, label: 'Onboarding Invitations' },
        { key: '/cio/customers', icon: <ShopOutlined />, label: 'Customers' },
        { key: '/cio/support', icon: <CustomerServiceOutlined />, label: 'Tech Support & Issues' },
      ],
    });
  }

  if (isPlatform || has(ROLES.CFO)) {
    items.push({
      key: 'grp-cfo',
      label: 'CFO Office',
      type: 'group',
      children: [
        { key: '/cfo', icon: <DollarOutlined />, label: 'Dashboard' },
        { key: '/cfo/pricing-plans', icon: <TagsOutlined />, label: 'Pricing Plans' },
        { key: '/cfo/invoices', icon: <FileTextOutlined />, label: 'Invoices' },
      ],
    });
  }

  // Support agents (without a CIO/Platform workspace) work their assigned tickets.
  if (has(ROLES.SUPPORT) && !isPlatform && !has(ROLES.CIO)) {
    items.push({
      key: 'grp-support',
      label: 'Support',
      type: 'group',
      children: [
        { key: '/cio/support', icon: <CustomerServiceOutlined />, label: 'Tech Support & Issues' },
      ],
    });
  }

  return items;
};

/** Flattened selectable keys, longest first, so detail routes highlight their parent. */
const selectableKeys = (items) =>
  items
    .flatMap((g) => (g.children || []).map((c) => c.key))
    .sort((a, b) => b.length - a.length);

const AppLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, roles } = useSelector(selectAuth);
  const screens = useBreakpoint();
  const [collapsed, setCollapsed] = useState(false);

  const menuItems = useMemo(() => buildMenu(roles), [roles]);
  const selectedKey = useMemo(() => {
    const keys = selectableKeys(menuItems);
    return keys.find((k) => location.pathname === k || location.pathname.startsWith(k + '/')) || '';
  }, [menuItems, location.pathname]);

  const crumbs = useMemo(() => buildCrumbs(location.pathname), [location.pathname]);
  const breadcrumbItems = crumbs.map((c, i) => ({
    key: c.path,
    title:
      i < crumbs.length - 1 ? (
        <span style={{ cursor: 'pointer' }} onClick={() => navigate(c.path)}>
          {c.label}
        </span>
      ) : (
        c.label
      ),
  }));

  const onLogout = () => {
    dispatch(loggedOut());
    navigate('/login', { replace: true });
  };

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Account';

  const userMenu = {
    items: [
      { key: 'email', label: user?.email, disabled: true },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Sign out', onClick: onLogout },
    ],
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        theme="light"
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
        width={248}
      >
        <div className="app-logo">
          <span className="mark">
            <img src={velorMark} alt="VelorHR" />
          </span>
          {!collapsed && (
            <span className="word">
              VelorHR<small>Administration</small>
            </span>
          )}
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: screens.xs ? '0 12px' : '0 24px',
            borderBottom: '1px solid #e6efd6',
          }}
        >
          <Breadcrumb items={breadcrumbItems} />
          <Dropdown menu={userMenu} placement="bottomRight">
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, cursor: 'pointer' }}>
              {!screens.xs && (
                <div style={{ textAlign: 'right', lineHeight: 1.25 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#101828' }}>{fullName}</div>
                  <div style={{ fontSize: 12, color: '#667085' }}>
                    {(roles || []).map((r) => ROLE_NAME_LABELS[r] || r).join(', ')}
                  </div>
                </div>
              )}
              <Avatar size={34} style={{ background: '#eaf4ec', color: '#599c16' }} icon={<UserOutlined />} />
            </div>
          </Dropdown>
        </Header>
        <Content className="app-content">{children}</Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
